import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@marriott/mi-ui-library';
import { ENTER_KEY } from '../../../constants';
import { useFocusOut } from '../../../hooks';
import { AttendeesStepper, RoomsStepper, TextFormField } from '../../index';
import {
  RoomsAndEventSpaceProps,
  RoomsAndEventSpace as RoomsAndEventSpaceType,
  Variant,
} from '../RoomsAndEventSpace.types';
import { StyledRoomsAndEventSpace } from '../RoomsAndEventSpace.styles';
import { useRoomsAndEventSpace } from '../useRoomsAndEventSpace';

export const DesktopRoomsAndEventSpace = React.forwardRef<HTMLInputElement, RoomsAndEventSpaceProps>((props, ref) => {
  const {
    labels,
    showErrorMessage = false,
    errorMessage = '',
    showHelperText = false,
    helperText = '',
    onChange,
    variant,
    value,
  } = props;

  const { guestRooms, attendees, guestRoomsAndEventSpace, reset, resetAriaLabel } = labels;

  const RoomsAndEventSpaceContainerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const {
    roomsAndEventSpace,
    increaseRooms,
    decreaseRooms,
    increaseAttendees,
    decreaseAttendees,
    inputRoomHandler,
    inputAttendeesHandler,
    handleReset,
  } = useRoomsAndEventSpace(value, onChange, variant);

  useFocusOut({
    ref: RoomsAndEventSpaceContainerRef.current,
    onFocusOut: (isFocusOut: boolean) => {
      if (isFocusOut) setIsOpen(false);
    },
    handleKeyup: true,
  });

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const firstInput = RoomsAndEventSpaceContainerRef.current?.querySelector<HTMLInputElement>(
      '.rooms-eventspace-container input:not([type="hidden"]):not([disabled])'
    );

    firstInput?.focus();
  }, [isOpen]);

  const showRooms = variant === Variant.ROOMS || variant === Variant.ROOMSANDEVENTSPACE;
  const showAttendees = variant === Variant.EVENTSPACE || variant === Variant.ROOMSANDEVENTSPACE;

  const getFormattedText = (count: number, singular: string, plural: string) =>
    count ? `${count} ${count === 1 ? singular : plural}` : '';

  const getInputDisplayText = (): string => {
    const parts: string[] = [];
    const roomText = getFormattedText(roomsAndEventSpace.rooms, guestRooms.room, guestRooms.rooms);
    if (roomText) parts.push(roomText);
    const attendeeText = getFormattedText(roomsAndEventSpace.eventAttendees, attendees.attendee, attendees.attendees);
    if (attendeeText) parts.push(attendeeText);
    return parts.join(': ');
  };

  const handleClick = () => {
    setIsOpen(true);
  };

  const handleKeydown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === ENTER_KEY) {
      event.preventDefault();
      event.stopPropagation();
      setIsOpen(!isOpen);
    }
  };

  const inputLabel =
    variant === Variant.ROOMS
      ? guestRooms.label
      : variant === Variant.EVENTSPACE
      ? attendees.label
      : guestRoomsAndEventSpace.label;

  const inputAriaLabel =
    variant === Variant.ROOMS
      ? guestRooms.ariaLabel
      : variant === Variant.EVENTSPACE
      ? attendees.ariaLabel
      : guestRoomsAndEventSpace.ariaLabel;

  const inputPlaceholder =
    variant === Variant.ROOMS
      ? guestRooms.placeholder
      : variant === Variant.EVENTSPACE
      ? attendees.placeholder
      : guestRoomsAndEventSpace.placeHolderText;

  return (
    <StyledRoomsAndEventSpace
      data-testid="groups-RoomsAndEventSpace"
      data-component-name="m-groups-RoomsAndEventSpace"
      ref={RoomsAndEventSpaceContainerRef}
    >
      <TextFormField
        type="text"
        className="m-input-text-field"
        label={inputLabel}
        ariaLabel={inputAriaLabel}
        value={getInputDisplayText()}
        ref={ref}
        hideCursor={true}
        showErrorMessage={showErrorMessage}
        errorMessage={errorMessage}
        showHelperText={showHelperText}
        helperText={helperText}
        placeholder={inputPlaceholder}
        onClick={handleClick}
        onKeyDown={handleKeydown}
      />

      {isOpen ? (
        <div
          className="m-dropdown-container dropdown-container rooms-eventspace-container"
          data-testid="rooms-eventspace-flyout"
          role="dialog"
        >
          <div className="py-3 custom-scrollbar m-dropdown-content">
            {showRooms && (
              <>
                <span className="t-subtitle-s">{guestRooms.label}</span>
                <div className="stepper-container">
                  <RoomsStepper
                    labels={guestRooms}
                    value={roomsAndEventSpace.rooms}
                    onIncrement={increaseRooms}
                    onDecrement={decreaseRooms}
                    onInputChange={inputRoomHandler}
                  />
                </div>
              </>
            )}

            {showAttendees && (
              <>
                <span className="t-subtitle-s">{attendees.label}</span>
                <div className="stepper-container">
                  <AttendeesStepper
                    labels={attendees}
                    value={roomsAndEventSpace.eventAttendees}
                    onIncrement={increaseAttendees}
                    onDecrement={decreaseAttendees}
                    onInputChange={inputAttendeesHandler}
                  />
                </div>
              </>
            )}

            <div className="text-left py-2">
              <Button
                buttonCopy={reset}
                callback={handleReset}
                className="m-link-action reset-cta"
                ariaLabel={resetAriaLabel}
              />
            </div>
          </div>
        </div>
      ) : null}
    </StyledRoomsAndEventSpace>
  );
});
